// NATIVE
import { useEffect, useState } from 'react';

// INSTALLED
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import { Box, Button, Divider, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

// CUSTOM
import { UploadButtonField } from 'components/formFields/uploadButtonField';
import { mtDividerUpTable } from '../../../commons';
import { hasPemissions } from 'commons/utils';
import { TableEmpleados } from './components/TableEmpleados';
import { SnackAlert } from 'components/SnackAlert';
import { BreadCrumbsAndTitle } from 'components/generics/BreadCrumbsAndTitle';
import { AlertsInformativos } from 'components/generics/AlertsInformativos';
import { alertInformativo } from 'commons/texts';
import { PageLayout } from 'components/generics/PageLayout';
import { InfoModal } from 'components/generics/InfoModal';
import { startCreateMassiveEmployee } from '../../../store/employee/thunks';

/**
 * Employees component renders the employee listing page.
 *
 * This component handles the following functionalities:
 * - Checks if the user has permission to create a new employee.
 * - Displays a snackbar alert for notifications.
 * - Renders breadcrumbs and title for navigation.
 * - Conditionally renders a button to create a new employee if the user has the required permissions.
 * - Renders a table of employees.
 *
 * @component
 * @returns {JSX.Element} The Employees component.
 *
 * @example
 * // Usage example
 * <Employees />
 */
export const Employees = () => {
  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);

  const [alert_snack, setAlertSnack] = useState(false);
  const [msgSnack, setMsgSnack] = useState({
    msg: '',
    status: 'success',
  });

  // USE EFECT
  useEffect(() => {
    canCheckCanCreate();
  }, []);

  const canCheckCanCreate = async () => {
    const havePermissions = await hasPemissions('modulo.thumano', 'modulo.empleados', 'create');

    setCanCreate(havePermissions);
  };

  const [openInfoModal, setOpenInfoModal] = useState(false);

  return (
    <>
      <PageLayout
        title="Listado Empleados"
        mainModule="Talento Humano"
        icon="PeopleAlt"
        infoAlert={alertInformativo.empleado}
        actionButtons={
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <IconButton color="primary" onClick={() => setOpenInfoModal(true)}>
                <InfoOutlinedIcon />
              </IconButton>

              <Button
                variant="text"
                color="primary"
                size="small"
                startIcon={
                  <img src="/files/excell.svg" alt="Excel" style={{ width: 20, height: 20 }} />
                }
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                }}
                href="/formatos/formato-importacion-empelados.xlsx"
                download
              >
                Descargar formato
              </Button>

              <Formik initialValues={{ uploadExcel: '' }} onSubmit={() => {}}>
                {() => (
                  <UploadButtonField
                    name="uploadExcel"
                    accept=".xlsx, .xls, .csv"
                    text="Importación masiva"
                    icon={<UploadIcon fontSize="small" />}
                    style={{
                      borderRadius: '8px',
                      padding: '4px 10px',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                    onChange={async (e: any) => {
                      const file = e.target.files[0];
                      if (file) {
                        console.log('Archivo Excel seleccionado:', file.name);

                        if (file.size > 5 * 1024 * 1024) {
                          setMsgSnack({
                            msg: 'El archivo es muy pesado, máximo 5MB',
                            status: 'error',
                          });
                          setAlertSnack(true);
                          return;
                        }

                        const reader = new FileReader();
                        reader.onload = async (evt) => {
                          const bstr = evt.target?.result;
                          const wb = XLSX.read(bstr, { type: 'binary' });
                          const wsname = wb.SheetNames[0];
                          const ws = wb.Sheets[wsname];
                          const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

                          if (data.length > 0) {
                            const headers: any = data[0];
                            const requiredHeaders = [
                              'Nombres',
                              'Apellidos',
                              'Numero Documento',
                              'Email',
                            ];
                            const hasAllHeaders = requiredHeaders.every((h) => headers.includes(h));

                            if (!hasAllHeaders) {
                              setMsgSnack({
                                msg: 'El archivo no tiene la estructura requerida',
                                status: 'error',
                              });
                              setAlertSnack(true);
                              return;
                            }

                            const res = await dispatch(startCreateMassiveEmployee(file));
                            if (res && res.ok) {
                              setMsgSnack({
                                msg: 'Empleados creados correctamente',
                                status: 'success',
                              });
                              setAlertSnack(true);
                            } else {
                              setMsgSnack({
                                msg: 'Ocurrió un error al crear los empleados',
                                status: 'error',
                              });
                              setAlertSnack(true);
                            }
                          } else {
                            setMsgSnack({ msg: 'El archivo está vacío', status: 'error' });
                            setAlertSnack(true);
                          }
                        };
                        reader.readAsBinaryString(file);
                      }
                    }}
                  />
                )}
              </Formik>
            </Box>

            {/* Separador visual */}
            {canCreate && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 1, height: '32px', alignSelf: 'center' }}
              />
            )}

            {/* Acción Principal */}
            {canCreate && (
              <Button
                component={Link}
                to={'/talento-humano/crear-empleado'}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0px 4px 8px -4px rgba(105, 108, 255, 0.42)',
                }}
              >
                Crear Empleado
              </Button>
            )}
          </>
        }
      >
        <SnackAlert
          open={alert_snack}
          onclick={setAlertSnack}
          text={msgSnack.msg}
          severity={msgSnack.status}
        />
        <TableEmpleados />
      </PageLayout>

      <InfoModal
        open={openInfoModal}
        setOpen={setOpenInfoModal}
        title="Información sobre Importación Masiva"
        description="La importación masiva permite registrar usuarios de forma rápida mediante un archivo de Excel."
        infoText={
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              La plantilla descargable es el formato exacto de cómo se deben estructurar los datos.
              Ten en cuenta que mediante este método de creación rápida solo se cargarán los datos
              básicos del empleado (sin archivos adjuntos).
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 2 }}>
              Pasos a seguir:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              <li>
                <strong>Paso 1:</strong> Descarga la plantilla.
              </li>
              <li>
                <strong>Paso 2:</strong> Agrega la información.
              </li>
              <li>
                <strong>Paso 3:</strong> Importa el archivo.
              </li>
            </Box>
          </Box>
        }
      />
    </>
  );
};
